import i18n from "../../i18n";
import { formatTranslation } from "../../shared/utils/translationUtils";
import ProductsBiologicalComponents from "./step-components/Products/Biological/ProductsBiologicalComponents";
import ProductsNutrientsComponent from "./step-components/Products/ProductsNutrientsComponent";
import ProductsSeedComponent from "./step-components/Products/ProductsSeedComponent";
import { type objectivesTargetType, type ProtocolStepItemType } from "./types";

export const protocolsSteps: ProtocolStepItemType[] = [
  {
    title: "Trial Type",
    path: "trial-type",
    stepNumber: 1,
  },
  {
    title: "Protocol Rules",
    path: "rules",
    stepNumber: 2,
  },

  {
    title: "Products",
    path: "products",
    stepNumber: 3,
  },
  {
    title: "Summary",
    path: "summary",
    stepNumber: 4,
  },
];

export const SUBMIT_PROTOCOL_LABEL = "Save and configure trial";
export const targets: objectivesTargetType[] = [
  {
    label: "createProtocol.objectives.objectiveItemTarget.yield",
    value: "Yield",
  },
  {
    label: "createProtocol.objectives.objectiveItemTarget.moisture",
    value: "Moisture",
  },
  {
    label: "createProtocol.objectives.objectiveItemTarget.peakNDVI",
    value: "Peak NDVI",
  },
  {
    label: "createProtocol.objectives.objectiveItemTarget.dryMatter",
    value: "Dry Matter",
  },
  {
    label:
      "createProtocol.objectives.objectiveItemTarget.yieldAtStandardMoisture",
    value: "Yield at standard moisture (15%)",
  },
  {
    label: "createProtocol.objectives.objectiveItemTarget.specificWeight",
    value: "Specific weight (kg/hl)",
  },
  {
    label: "createProtocol.objectives.objectiveItemTarget.plantHeight",
    value: "Plant height (cm)",
  },
  {
    label: "createProtocol.objectives.objectiveItemTarget.lodgingPercent",
    value: "Lodging %",
  },
  {
    label:
      "createProtocol.objectives.objectiveItemTarget.infectionByDiseasePercent",
    value: "% Infection by disease",
  },
  // {
  //   label: "Seed Rate",
  //   value: "seed_rate",
  // },
  // {
  //   label: "Sunflower Rate",
  //   value: "sunflower_rate",
  // },
  // {
  //   label: "Silage Rate",
  //   value: "silage rate",
  // },
  // {
  //   label: "Cereals Rate",
  //   value: "cereals rate",
  // },
  // {
  //   label: "Fert Rate",
  //   value: "fert rate",
  // },
];

export const targetsLabels: Record<string, string> = {
  Yield: "createProtocol.objectives.objectiveItemTarget.yield",
  Moisture: "createProtocol.objectives.objectiveItemTarget.moisture",
  "Peak NDVI": "createProtocol.objectives.objectiveItemTarget.peakNDVI",
  "Dry Matter": "createProtocol.objectives.objectiveItemTarget.dryMatter",
  "Yield at standard moisture (15%)":
    "createProtocol.objectives.objectiveItemTarget.yieldAtStandardMoisture",
  "Specific weight (kg/hl)":
    "createProtocol.objectives.objectiveItemTarget.specificWeight",
  "Plant height (cm)":
    "createProtocol.objectives.objectiveItemTarget.plantHeight",
  "Lodging %": "createProtocol.objectives.objectiveItemTarget.lodgingPercent",
  "% Infection by disease":
    "createProtocol.objectives.objectiveItemTarget.infectionByDiseasePercent",
};

export const getDummyObjectives = (): any[] => [
  {
    name: formatTranslation(
      i18n.t("createProtocol.dummyObjectives.objective1.name")
    ),
    target: formatTranslation(
      i18n.t("createProtocol.dummyObjectives.cropEstablishment")
    ),
    description: formatTranslation(
      i18n.t("createProtocol.dummyObjectives.objective1.description")
    ),
  },
  {
    name: formatTranslation(
      i18n.t("createProtocol.dummyObjectives.objective2.name")
    ),
    target: formatTranslation(
      i18n.t("createProtocol.dummyObjectives.cropEstablishment")
    ),
    description: formatTranslation(
      i18n.t("createProtocol.dummyObjectives.objective2.description")
    ),
  },
];

export const getMeasureUnitOptions = (): any[] => [
  {
    value: "seeds/m2",
    label: (
      <>
        {formatTranslation(i18n.t("createProtocol.measureUnit.seedsPerM2"))}
        <sup>2</sup>
      </>
    ),
  },
  {
    value: "seeds/ha",
    label: <>{formatTranslation(i18n.t("newTrial.ratesDosage.unit1"))}</>,
  },
  {
    value: "kg/ha",
    label: <>{formatTranslation(i18n.t("newTrial.ratesDosage.unit3"))}</>,
  },
];

export const getNutrientTypeOptions = (): any[] => [
  {
    label: (
      <span className="nutrient-type-category">
        {formatTranslation(i18n.t("createProtocol.nutrients.basicNutrients"))}
      </span>
    ),
    title: formatTranslation(i18n.t("createProtocol.nutrients.basicNutrients")),
    options: [
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.nitrogen"))}
          </span>
        ),
        value: "N",
        key: "nitrogen",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.phosphorus"))}
          </span>
        ),
        value: "P",
        key: "phosphorusPentoxide",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.potassium"))}
          </span>
        ),
        value: "K",
        key: "potassiumOxide",
      },
    ],
  },
  {
    label: (
      <span className="nutrient-type-category">
        {formatTranslation(
          i18n.t("createProtocol.nutrients.additionalNutrients")
        )}
      </span>
    ),
    title: formatTranslation(
      i18n.t("createProtocol.nutrients.additionalNutrients")
    ),
    options: [
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.cadmium"))}
          </span>
        ),
        value: "Cd",
        key: "cadmium",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.chromium"))}
          </span>
        ),
        value: "Cr",
        key: "chromium",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.lead"))}
          </span>
        ),
        value: "Pb",
        key: "lead",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.mercury"))}
          </span>
        ),
        value: "Hg",
        key: "mercury",
      },
    ],
  },
  {
    label: (
      <span className="nutrient-type-category">
        {formatTranslation(i18n.t("createProtocol.nutrients.otherNutrients"))}
      </span>
    ),
    title: formatTranslation(i18n.t("createProtocol.nutrients.otherNutrients")),
    options: [
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.aluminum"))}
          </span>
        ),
        value: "Al",
        key: "aluminum",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.boron"))}
          </span>
        ),
        value: "B",
        key: "boron",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.calcium"))}
          </span>
        ),
        value: "Ca",
        key: "calcium",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.cobalt"))}
          </span>
        ),
        value: "Co",
        key: "cobalt",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.copper"))}
          </span>
        ),
        value: "Cu",
        key: "copper",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.iron"))}
          </span>
        ),
        value: "Fe",
        key: "iron",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.magnesium"))}
          </span>
        ),
        value: "Mg",
        key: "magnesium",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.manganese"))}
          </span>
        ),
        value: "Mn",
        key: "manganese",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.molybdenum"))}
          </span>
        ),
        value: "Mo",
        key: "molybdenum",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.nickel"))}
          </span>
        ),
        value: "Ni",
        key: "nickel",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.selenium"))}
          </span>
        ),
        value: "Se",
        key: "selenium",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.silicon"))}
          </span>
        ),
        value: "Si",
        key: "silicon",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.sodium"))}
          </span>
        ),
        value: "Na",
        key: "sodium",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.sulfur"))}
          </span>
        ),
        value: "S",
        key: "sulfur",
      },
      {
        label: (
          <span>
            {formatTranslation(i18n.t("createProtocol.nutrients.zinc"))}
          </span>
        ),
        value: "Zn",
        key: "zinc",
      },
    ],
  },
];

export const protocolProductTabs = (protocolType: string[]): any[] => {
  const tabs = [];
  // Maintain explicit order: Seed -> Biological -> Nutrient
  if (protocolType.includes("seed")) {
    tabs.push({
      key: "seed",
      label: formatTranslation(i18n.t("ratesDosage.tabs.seeds")),
      children: <ProductsSeedComponent />,
      forceRender: true,
    });
  }
  if (protocolType.includes("biological")) {
    tabs.push({
      key: "biological",
      label: formatTranslation(
        i18n.t("createProtocol.products.tabs.biologicals")
      ),
      children: <ProductsBiologicalComponents />,
      forceRender: true,
    });
  }
  if (protocolType.includes("nutrient")) {
    tabs.push({
      key: "nutrient",
      label: formatTranslation(
        i18n.t("createProtocol.products.nutrients.header")
      ),
      children: <ProductsNutrientsComponent />,
      forceRender: true,
    });
  }
  return tabs;
};

export const getBiologicalProductOptions = (): any[] => [
  {
    label: (
      <span>
        {formatTranslation(
          i18n.t("createProtocol.biologicalProducts.productOne")
        )}
      </span>
    ),
    value: "1",
    key: "product1",
  },
  {
    label: (
      <span>
        {formatTranslation(
          i18n.t("createProtocol.biologicalProducts.productTwo")
        )}
      </span>
    ),
    value: "2",
    key: "product1",
  },
  {
    label: (
      <span>
        {formatTranslation(
          i18n.t("createProtocol.biologicalProducts.productThree")
        )}
      </span>
    ),
    value: "3",
    key: "product1",
  },
  {
    label: (
      <span>
        {formatTranslation(
          i18n.t("createProtocol.biologicalProducts.productFour")
        )}
      </span>
    ),
    value: "4",
    key: "product1",
  },
  {
    label: (
      <span>
        {formatTranslation(
          i18n.t("createProtocol.biologicalProducts.productFive")
        )}
      </span>
    ),
    value: "5",
    key: "product1",
  },
];
